import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {CaretDown, FolderSimple} from '../../icons';
import {AccountChip} from './ClientProposalRow';
import {AssignToPicker} from './AssignToPicker';
import {isClosedEntry, isUnassignedByUser} from '../clientGroups';
import {REASON_DORMANT, REASON_NO_RECENT_SPEND, REASON_UNMATCHED} from '../wizardReducer';
import {bucketEntriesMatching, bucketMatchesFilter, FILTER_ALL, FILTER_BUCKET} from '../proposalListView';

/**
 * Lo que quedó afuera de los clientes a crear, en secciones POR MOTIVO.
 *
 * Está plegado y al final a propósito: no pueden ser lo primero que se vea, porque lo primero que se
 * ve define si el recorrido se siente resuelto o roto. El encabezado dice cuántas quedan afuera y
 * que se asigna sólo la que sea de un cliente actual: no es una lista para revisar entera (H-04).
 *
 * **Las tres secciones existen porque son tres problemas distintos y cada uno se resuelve distinto.**
 * Una cuenta sin actividad hace meses no es una cuenta cuyo nombre no entendimos: de la primera
 * sabemos perfectamente de quién es y la dejamos afuera por una decisión de producto —una línea de
 * control vigila un presupuesto que se está gastando—, y de la segunda no sabemos nada. Mezcladas en
 * una sola lista, la explicación de cada fila era la única señal de que no eran lo mismo, y el
 * usuario tenía que leer las cincuenta para descubrirlo.
 */
export function ReviewBucket({pending, removed, groups, dispatch, query, filter = FILTER_ALL,
                              focusSignal = 0, onReveal = null}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const box = useRef(null);

    /**
     * El chip de arriba pide traer acá: se abre el cajón y se trae a la vista.
     *
     * El chip anuncia el trabajo pendiente ARRIBA de todo y el cajón vive al final, después de la
     * lista entera — con seiscientas filas en el medio, enterarse de que hay 225 cuentas por
     * revisar y no tener cómo llegar a ellas es peor que no enterarse.
     *
     * El scroll va en un `requestAnimationFrame` y no derecho: `setOpen` recién se aplica en el
     * render siguiente, así que corriendo ahora mismo se mediría la caja PLEGADA y el destino
     * quedaría a cientos de píxeles del contenido que se acaba de abrir.
     */
    useEffect(() => {
        if (!focusSignal) return;
        setOpen(true);
        const frame = requestAnimationFrame(() => {
            if (box.current?.scrollIntoView) box.current.scrollIntoView({behavior: 'smooth', block: 'start'});
        });
        return () => cancelAnimationFrame(frame);
    }, [focusSignal]);

    // Elegir la lente del cajón es pedir verlo: dejarlo plegado sería contestar con la caja cerrada.
    // Las subsecciones quedaban «escondidísimas» justamente por esto — para llegar a ellas había que
    // saber de antemano que estaban ahí, detrás de un acordeón al final de seiscientas filas.
    useEffect(() => {
        if (filter === FILTER_BUCKET) setOpen(true);
    }, [filter]);
    /**
     * El cajón obedece a la MISMA lente que la lista: búsqueda y filtro.
     *
     * La búsqueda ya llegaba (con `bucketEntriesMatching`, el mismo criterio que arriba, para que
     * una cuenta no aparezca en una superficie y no en la otra con el mismo término). El FILTRO no,
     * y esa mitad faltante es lo que hacía que la barra se sintiera rota: Pablo filtró por «listos
     * para crear» esperando ver sus cuatro elegidos, y el bloque de revisión siguió mostrando sus
     * ochocientas filas — «me quedé en la de revisión».
     *
     * Ninguna cuenta de acá puede estar «lista para crear» ni tener dudas, así que esas lentes lo
     * dejan vacío. No desaparece: se queda diciendo cuántas escondió, que es lo que convierte el
     * filtro en una respuesta en vez de en un botón que no hace nada.
     */
    const reachedByLens = bucketMatchesFilter(filter);
    const allWaiting = pending || [];
    const allOfRemoved = removed || [];
    // La lente decide qué se DIBUJA, no qué contiene el cajón. Por eso los conjuntos se arman
    // siempre y es el cuerpo el que no se renderiza: con el filtro puesto, un encabezado calculado
    // sobre listas vacías decía «0 cuentas que dejaste afuera» sobre un cajón de 899 — falso dos
    // veces, ni son 0 ni las sacó el usuario.
    const waiting = bucketEntriesMatching(allWaiting, query);
    const allRemoved = bucketEntriesMatching(allOfRemoved, query);
    const hidden = reachedByLens
        ? (allWaiting.length + allOfRemoved.length) - (waiting.length + allRemoved.length)
        // Con una lente que no alcanza al cajón, lo escondido es todo lo que tiene.
        : waiting.length + allRemoved.length;
    // Las que la PLATAFORMA reporta muertas van aparte de las que sacó el usuario: comparten el
    // gesto de recuperación pero no el motivo, y decirle «la sacaste» a alguien que no la sacó es
    // exactamente el tipo de mentira chica que hace desconfiar del resto de la pantalla.
    const isDormantEntry = entry => entry.decision.reason === REASON_DORMANT;
    // Las que no invirtieron en los últimos 7 días (C-02): el backend no las propone. Sin dueño, como
    // las dormidas sin dueño, así que viajan en `unassigned`.
    const isNoSpendEntry = entry => entry.decision.reason === REASON_NO_RECENT_SPEND;
    const noSpend = waiting.filter(isNoSpendEntry).sort(byChannelAndName);
    const platformDisabled = allRemoved.filter(isClosedEntry).sort(byChannelAndName);
    // Lo que el USUARIO sacó, por los dos caminos: el tacho (la cuenta queda descartada) y «Sacar de
    // este cliente» (la cuenta espera otro dueño). Son el mismo gesto para quien lo hizo, así que
    // viven en la misma sección y cuentan en el mismo número (card 2yINyKQv). Primero las que
    // esperan dueño, que piden algo; después las descartadas, que sólo ofrecen deshacer.
    //
    // Ni las cerradas ni las dormidas, que las apartó el sistema: decirle «la sacaste» a alguien que
    // no la sacó es el tipo de mentira chica que hace desconfiar del resto.
    const unassignedByUser = waiting.filter(isUnassignedByUser).sort(byChannelAndName);
    const dismissedByUser = allRemoved
        .filter(entry => !isClosedEntry(entry) && !isDormantEntry(entry))
        .sort(byChannelAndName);
    const takenCount = unassignedByUser.length + dismissedByUser.length;
    // Las dormidas llegan por DOS caminos —sin dueño viajan en `unassigned`, porque el agrupador
    // las aparta antes de agrupar; con dueño las descarta `decisionFromAccount`— y para el usuario
    // son la misma cosa. Repartidas en dos secciones, la misma pregunta se contestaría dos veces.
    const dormant = waiting.filter(isDormantEntry)
        .concat(allRemoved.filter(isDormantEntry))
        .sort(byChannelAndName);
    const unknown = waiting
        .filter(entry => !isDormantEntry(entry) && !isNoSpendEntry(entry) && !isUnassignedByUser(entry))
        .sort(byChannelAndName);
    // `unknown` se parte SÓLO para dibujar. Las de nombre legible van en su propia sección porque
    // titularlas «No supimos de quién son» era justamente la queja («si la cuenta se llama Iga y la
    // empresa se llama Iga…»).
    const unmatched = unknown.filter(entry => entry.decision.reason === REASON_UNMATCHED);
    const opaque = unknown.filter(entry => entry.decision.reason !== REASON_UNMATCHED);

    // Los números del ENCABEZADO salen de lo que el cajón CONTIENE, sin lente. El cuerpo sí obedece
    // a la lente, pero el título describe la caja: con una búsqueda sin coincidencias el conteo
    // filtrado daba 0 y el título caía en «0 cuentas que dejaste afuera» — falso dos veces, ni son
    // 0 ni las dejó afuera el usuario. Es el mismo defecto que ya se había arreglado para el filtro
    // y que volvió por el otro camino: las dos lentes titulan igual.
    const allClosed = allOfRemoved.filter(isClosedEntry).length;
    /**
     * Cuántas cuentas pide revisar este cajón — el número del título.
     *
     * Es TODO lo que no está cerrado, con la misma cuenta que el chip de arriba y el subtítulo
     * (`bucketCountsOf`): lo que espera dueño, las dormidas —también las que bajan por `removed`
     * porque conservan su cliente— y lo que el usuario sacó, con el tacho o a mano. Cada vez que
     * este número contó una población distinta de la que dibujaba, la pantalla dio dos números para
     * lo mismo: «Revisa estas 218» sobre 231 filas en la agencia 130, o «Fuera de la cartera (998)»
     * sobre «26 + 967» en la de Leo.
     *
     * Las cerradas siguen contándose aparte, en su propia frase: no piden ninguna decisión, y
     * sumarlas convertiría un pedido de 231 en uno de 899.
     */
    const toReview = allWaiting.length + allOfRemoved.length - allClosed;
    // Cuántas filas va a dibujar el cuerpo con la lente puesta. Distinto de `toReview`, que describe
    // la caja entera.
    const dibuja = noSpend.length + dormant.length + unknown.length + platformDisabled.length + takenCount;

    // Con la búsqueda puesta el cajón NO desaparece cuando no coincide nada: desaparecer sería
    // volver a mentir, sólo que al revés («no está en el cajón» cuando sí está). Se queda diciendo
    // cuántas escondió el filtro.
    if (waiting.length === 0 && allRemoved.length === 0 && !hidden) return null;

    // Abrir el cajón cuando la lente no lo alcanza es pedir verlo: la pantalla cambia a la lente del
    // cajón en vez de abrir una caja vacía. Pasaba con «Listos para crear» puesto: el cajón se
    // desplegaba sin nada adentro.
    const toggle = () => {
        if (!open && !reachedByLens && onReveal) onReveal();
        setOpen(!open);
    };

    return (
        <section className={`ob-bucket${open ? ' -open' : ''}`} ref={box}>
            <button type="button" className="ob-bucket__head" onClick={toggle}
                    aria-expanded={open}>
                <FolderSimple size={16}/>
                <span className="ob-bucket__title">
                    {/* Sin nada que revisar, lo único que queda son las cerradas: el título las
                        nombra por lo que son. Decía «N cuentas que dejaste afuera», que le atribuía
                        al usuario lo que reportó la plataforma. */}
                    {/* Con algo más, el título es UN número —todo lo que quedó afuera, cerradas
                        incluidas— y ya no pide revisar (H-04, recorrido con Pablo del 13/10): «Revisa
                        estas 220» sobre cuentas que conviene ignorar era un pedido falso. Es el mismo
                        total que el resumen de arriba y que el filtro «Quedan afuera (N)». */}
                    {toReview > 0
                        ? t('onboarding.clients.bucket_title', {count: toReview + allClosed})
                        : t('onboarding.clients.bucket_group_platform_disabled', {count: allClosed})}
                </span>
                <span className="ob-bucket__hint">
                    {/* Por qué está vacío importa: «tu filtro no alcanza a estas cuentas» es una
                        respuesta, «no hay resultados» sobre las mismas 852 filas es un botón roto. */}
                    {/* La invitación a asignar sólo con algo asignable: a las cerradas solas no se
                        les asigna nada, se las «usa igual». */}
                    {hidden > 0
                        ? t(reachedByLens
                            ? 'onboarding.clients.bucket_hidden_by_search'
                            : 'onboarding.clients.bucket_hidden_by_filter', {count: hidden})
                        : toReview > 0 ? t('onboarding.clients.bucket_hint') : null}
                </span>
                <CaretDown size={14} className="ob-bucket__caret"/>
            </button>

            {/* Abierto con una búsqueda que no dejó nada, el cajón LO DICE adentro. Antes no se
                dibujaba el cuerpo y lo único que cambiaba era la flecha: Leo abrió «Revisa estas 26
                cuentas» con «perfumerías rouge» escrito y no se desplegó nada, ni un cero (card
                03NF0The). El hint del encabezado lo decía, pero no es donde se mira al abrir. */}
            {open && reachedByLens && dibuja === 0 && query && (
                <div className="ob-bucket__body">
                    <p className="ob-bucket__empty" role="status">
                        {t('onboarding.clients.bucket_no_matches',
                           {query: query.trim(), interpolation: {escapeValue: false}})}
                    </p>
                </div>
            )}

            {open && reachedByLens && dibuja > 0 && (
                <div className="ob-bucket__body">
                    {/* Las que no invirtieron en la semana van primero (C-02): en una agencia con
                        muchas cuentas viejas son la mayoría del cajón, y la pregunta es la misma para
                        todas —¿alguna es de un cliente actual?—, así que la sección la dice una vez. */}
                    {noSpend.length > 0 && (
                        <BucketGroup titleKey="bucket_group_no_spend" hintKey="bucket_group_no_spend_hint"
                                     count={noSpend.length}>
                            {noSpend.map(entry => (
                                <BucketRow key={entry.pk} entry={entry} groups={groups} dispatch={dispatch}
                                           withReason={false}/>
                            ))}
                        </BucketGroup>
                    )}

                    {/* Las dormidas: tienen una respuesta clara («si la vas a reactivar, asígnala») y
                        la sección lo dice una vez, en vez de repetir el motivo en cada fila. */}
                    {dormant.length > 0 && (
                        <BucketGroup titleKey="bucket_group_dormant" hintKey="bucket_group_dormant_hint"
                                     count={dormant.length}>
                            {/* Dos clases de dormida conviven acá y piden acciones distintas: la
                                que no tiene dueño hay que asignarla, y la que ya lo tiene sólo
                                puede volver a la cartera. Ofrecerle «Asignar» y «Crear cliente» a
                                una cuenta que ya cuelga de un cliente son dos botones que no
                                significan nada. */}
                            {dormant.map(entry => (entry.decision.clientRef
                                ? <OwnedDormantRow key={entry.pk} entry={entry} dispatch={dispatch}/>
                                : <BucketRow key={entry.pk} entry={entry} groups={groups}
                                             dispatch={dispatch} withReason={false}/>))}
                        </BucketGroup>
                    )}

                    {/* Van acá arriba, con las dormidas, porque son lo mismo en naturaleza: una
                        condición de la cuenta que Dashbo LEYÓ, no un gesto de nadie. Y como el
                        motivo varía por fila (cancelada, suspendida, archivada), cada una lo dice:
                        es el dato que le permite al usuario saber si la va a poder reactivar. */}
                    {platformDisabled.length > 0 && (
                        <BucketGroup titleKey="bucket_group_platform_disabled"
                                     hintKey="bucket_group_platform_disabled_hint"
                                     count={platformDisabled.length}>
                            {platformDisabled.map(entry => (
                                <div className="ob-bucket__row -dismissed" key={entry.pk}>
                                    <AccountChip account={entry.account}/>
                                    <span className="ob-bucket__reason">
                                        {entry.decision.platformReason
                                            || t('onboarding.clients.reason.platform_disabled')}
                                    </span>
                                    <button type="button" className="ob-link"
                                            onClick={() => dispatch({type: 'RESTORE_ACCOUNT', pk: entry.pk})}>
                                        {t('onboarding.clients.use_anyway')}
                                    </button>
                                </div>
                            ))}
                        </BucketGroup>
                    )}

                    {/* Antes que las opacas: de estas sabemos cómo se llamaría el cliente, así que
                        resolverlas es un clic. La sección dice el motivo una vez. */}
                    {unmatched.length > 0 && (
                        <BucketGroup titleKey="bucket_group_unmatched" hintKey="bucket_group_unmatched_hint"
                                     count={unmatched.length}>
                            {unmatched.map(entry => (
                                <BucketRow key={entry.pk} entry={entry} groups={groups} dispatch={dispatch}
                                           withReason={false}/>
                            ))}
                        </BucketGroup>
                    )}

                    {opaque.length > 0 && (
                        <BucketGroup titleKey="bucket_group_unknown" count={opaque.length}>
                            {opaque.map(entry => (
                                <BucketRow key={entry.pk} entry={entry} groups={groups} dispatch={dispatch} withReason/>
                            ))}
                        </BucketGroup>
                    )}

                    {/* Sacar tiene vuelta atrás: sin esto era la única acción irreversible del
                        recorrido, y la pantalla promete que nada lo es. Las que salieron de un
                        cliente con «Sacar» no están eliminadas —esperan otro dueño—, así que llevan
                        las acciones de asignar y NO el tachado, que es sólo para lo eliminado. */}
                    {takenCount > 0 && (
                        <BucketGroup titleKey="bucket_group_removed" count={takenCount}>
                            {unassignedByUser.map(entry => (
                                <BucketRow key={entry.pk} entry={entry} groups={groups} dispatch={dispatch}
                                           withReason
                                           reason={t('onboarding.clients.removed_from',
                                                     {client: entry.decision.unassignedFrom,
                                                      interpolation: {escapeValue: false}})}/>
                            ))}
                            {dismissedByUser.map(entry => (
                                <div className="ob-bucket__row -dismissed" key={entry.pk}>
                                    <AccountChip account={entry.account}/>
                                    <span className="ob-bucket__reason">
                                        {entry.clientName
                                            ? t('onboarding.clients.removed_from',
                                                {client: entry.clientName,
                                                 interpolation: {escapeValue: false}})
                                            : t('onboarding.clients.dismissed')}
                                    </span>
                                    <button type="button" className="ob-link"
                                            onClick={() => dispatch({type: 'RESTORE_ACCOUNT', pk: entry.pk})}>
                                        {t('onboarding.clients.undo')}
                                    </button>
                                </div>
                            ))}
                        </BucketGroup>
                    )}
                </div>
            )}
        </section>
    );
}

/**
 * Una cuenta dormida que YA tiene cliente en la base.
 *
 * No pasa por el agrupador —su dueño ya existe— así que no necesita las acciones de asignación: lo
 * único que el usuario puede querer es devolverla a la cartera. Ofrece el mismo «Usar igual» que las
 * cerradas, y nombra al cliente para que se entienda qué vuelve.
 */
function OwnedDormantRow({entry, dispatch}) {
    const {t} = useTranslation();

    return (
        <div className="ob-bucket__row">
            <AccountChip account={entry.account}/>
            <span className="ob-bucket__reason">
                {t('onboarding.clients.dormant_of_client',
                   {client: entry.clientName, interpolation: {escapeValue: false}})}
            </span>
            <button type="button" className="ob-link"
                    onClick={() => dispatch({type: 'RESTORE_ACCOUNT', pk: entry.pk})}>
                {t('onboarding.clients.use_anyway')}
            </button>
        </div>
    );
}

/**
 * El orden de las secciones del cajón: por plataforma y después por nombre.
 *
 * Lo pidió Pablo mirando las cerradas —«no están en orden alfabético»— y vale para las tres: el
 * orden en que las devolvió el backend es el del agrupamiento, que sobre cientos de cuentas
 * apagadas no significa nada para quien está buscando una en particular. Agrupar por plataforma
 * primero es lo que hace que la lista se pueda recorrer: el usuario viene del administrador de
 * anuncios de UNA plataforma.
 *
 * El nombre puede ser `null` (hay cuentas sin nombre en producción), y entonces se ordena por el id
 * — que es lo que la fila muestra en su lugar, así que el orden coincide con lo que se lee.
 */
function byChannelAndName(one, other) {
    const channel = String(one.account?.channel || '')
        .localeCompare(String(other.account?.channel || ''));
    if (channel !== 0) return channel;
    // `numeric` porque los nombres de cuenta llevan números y el orden de texto los desordena:
    // «JHN 05» quedaba antes que «JHN 4», y «2873003711» antes que «360 Futbol». Quien busca en una
    // lista de cientos lee el número como número.
    return labelOf(one.account).localeCompare(labelOf(other.account), 'es', {numeric: true});
}

function labelOf(account) {
    return String(account?.name || account?.account_id || '');
}

/**
 * Una sección del cajón: el motivo dicho una vez, arriba, y las filas debajo.
 *
 * Exportada porque el modal «Desde tus cuentas conectadas» del Panel arma el mismo cajón (dormidas y
 * cerradas) con su propio copy: `ns` es el namespace de sus claves.
 */
export function BucketGroup({titleKey, hintKey, count, ns = 'onboarding.clients', children}) {
    const {t} = useTranslation();

    return (
        <div className="ob-bucket__group">
            <h3 className="ob-bucket__group-title">{t(`${ns}.${titleKey}`, {count})}</h3>
            {hintKey && <p className="ob-bucket__group-hint">{t(`${ns}.${hintKey}`)}</p>}
            {children}
        </div>
    );
}

/**
 * Una fila del cajón.
 *
 * Sin motivo, la fila es de DOS columnas y no de tres con una vacía. En la sección de las dormidas
 * el motivo es el mismo para todas y ya lo dijo el encabezado, así que la columna del medio quedaba
 * vacía en 213 filas seguidas: unos 340 px de blanco entre el nombre y el primer botón. Se saca la
 * columna en vez de rellenarla con un dato inventado — qué dato merece ese lugar (última actividad,
 * canal) es una decisión de producto, no de terminación.
 */
function BucketRow({entry, groups, dispatch, withReason, reason = null}) {
    const {t} = useTranslation();
    const [picking, setPicking] = useState(false);

    return (
        <div className={`ob-bucket__row${withReason ? '' : ' -no-reason'}`}>
            <AccountChip account={entry.account}/>
            {withReason && (
                <span className="ob-bucket__reason">
                    {/* Con `defaultValue`: un motivo nuevo del backend imprimiría su clave cruda en
                        pantalla, que es peor que una explicación genérica. El que llega armado
                        (`reason`) es el de lo que sacó el usuario, que lleva el nombre del cliente. */}
                    {reason || t(`onboarding.clients.reason.${(entry.decision.reason || 'OPAQUE').toLowerCase()}`,
                                 t('onboarding.clients.reason.opaque'))}
                </span>
            )}
            <div className="ob-bucket__actions">
                <div className="ob-picker-anchor">
                    <button type="button" className="ob-btn -mini -ghost" onClick={() => setPicking(!picking)}>
                        {t('onboarding.clients.assign')}
                    </button>
                    {picking && (
                        <AssignToPicker
                            groups={groups}
                            defaultName={entry.account.name || ''}
                            onPick={clientRef => {
                                dispatch({type: 'ASSIGN_ACCOUNT', pk: entry.pk, clientRef});
                                setPicking(false);
                            }}
                            onClose={() => setPicking(false)}/>
                    )}
                </div>
                {/* Ancho fijo (`.ob-bucket__create`): con el nombre adentro el botón cambiaba de ancho
                    fila por fila y corría «Asignar…», que arrancaba en una x distinta en cada una. */}
                <button type="button" className="ob-btn -mini -ghost ob-bucket__create"
                        onClick={() => dispatch({type: 'ASSIGN_ACCOUNT', pk: entry.pk,
                                                 clientRef: {type: 'new', name: suggestedNameOf(entry)}})}>
                    {/* Con nombre sugerido, el botón lo dice: es lo que va a crear. Pasa en
                        cualquier sección —también una opaca puede traer `suggested_client_name`—, y
                        sin sugerido no hay nombre que prometer y queda el genérico. */}
                    <span className="ob-bucket__create-label">
                        {entry.decision.proposedName
                            ? t('onboarding.clients.create_client_named',
                                {name: entry.decision.proposedName, interpolation: {escapeValue: false}})
                            : t('onboarding.clients.create_client')}
                    </span>
                </button>
                <button type="button" className="ob-link -muted"
                        onClick={() => dispatch({type: 'DISMISS_ACCOUNT', pk: entry.pk})}>
                    {t('onboarding.clients.dismiss')}
                </button>
            </div>
        </div>
    );
}

function suggestedNameOf(entry) {
    return entry.decision.proposedName || entry.account.name || String(entry.account.account_id);
}
